import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Globe2, Mail, MapPin, Menu, MessageCircle, X } from "lucide-react";
import { CONTACT_EMAIL, WHATSAPP_DISPLAY, brand, DOMAIN } from "../config/site";
import { navLinks } from "../data/siteContent";
import { defaultWhatsAppMessage, whatsappUrl } from "../lib/whatsapp";
import BrandLogo from "./BrandLogo";

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    setOpen(false);
  }, [pathname, hash]);

  return (
    <div className="app-shell" data-testid="app-shell">
      <header className="site-header" data-testid="site-header">
        <div className="container header-inner">
          <Link to="/" className="brand-lockup" data-testid="header-brand-link" onClick={() => setOpen(false)}>
            <BrandLogo variant="header" testId="header-brand-logo" />
          </Link>
          <nav className="desktop-nav" aria-label="Main navigation" data-testid="desktop-navigation">
            {navLinks.map((link) => (
              <NavLink key={link.path} to={link.path} className="nav-link" data-testid={`header-${link.testId}-link`}>
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="header-actions">
            <a className="btn btn-yellow desktop-whatsapp" href={whatsappUrl(defaultWhatsAppMessage)} data-testid="header-whatsapp-button" target="_blank" rel="noopener noreferrer">
              Get Support
            </a>
            <button className="btn btn-ghost mobile-menu-button" type="button" onClick={() => setOpen((value) => !value)} aria-label="Toggle menu" data-testid="mobile-menu-toggle-button">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        <div className="container">
          <nav className={`mobile-nav ${open ? "open" : ""}`} aria-label="Mobile navigation" data-testid="mobile-navigation">
            {navLinks.map((link) => (
              <NavLink key={link.path} to={link.path} className="nav-link" data-testid={`mobile-${link.testId}-link`} onClick={() => setOpen(false)}>
                {link.label}
              </NavLink>
            ))}
            <a className="btn btn-yellow" href={whatsappUrl(defaultWhatsAppMessage)} data-testid="mobile-whatsapp-button" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
              <MessageCircle size={18} /> Get Support
            </a>
          </nav>
        </div>
      </header>
      <main className="page-main" data-testid="page-main">{children}</main>
      <footer className="site-footer" data-testid="site-footer">
        <div className="container footer-grid">
          <div>
            <BrandLogo variant="footer" light testId="footer-brand-logo" />
            <p className="footer-tagline" data-testid="footer-tagline">{brand.tagline}</p>
            <div className="footer-contact-list" data-testid="footer-contact-details">
              <div data-testid="footer-service-line"><strong>Job Boost by Ben</strong></div>
              <div data-testid="footer-support-line">CV, interview and job-search support</div>
              <div data-testid="footer-domain-line"><Globe2 size={16} /> {DOMAIN}</div>
              <a href={whatsappUrl(defaultWhatsAppMessage)} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"><MessageCircle size={16} /> WhatsApp: {WHATSAPP_DISPLAY}</a>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email-link"><Mail size={16} /> Email: {CONTACT_EMAIL}</a>
              <div data-testid="footer-location-line"><MapPin size={16} /> London-based, supporting UK jobseekers online and locally</div>
            </div>
          </div>
          <nav className="footer-links" aria-label="Footer navigation" data-testid="footer-navigation">
            <Link to="/" data-testid="footer-home-link">Home</Link>
            <Link to="/services" data-testid="footer-services-link">Services</Link>
            <Link to="/packages" data-testid="footer-packages-link">Packages</Link>
            <a href="/#how-it-works" data-testid="footer-how-it-works-link">How it works</a>
            <Link to="/quiz" data-testid="footer-free-quiz-link">Free Quiz</Link>
            <Link to="/about" data-testid="footer-about-link">About</Link>
            <Link to="/contact" data-testid="footer-contact-link">Contact</Link>
            <Link to="/privacy-policy" data-testid="footer-privacy-policy-link">Privacy Policy</Link>
          </nav>
        </div>
        <div className="container footer-bottom" data-testid="footer-bottom-note">© {new Date().getFullYear()} Job Boost by Ben · <span data-testid="footer-domain">{DOMAIN}</span> · Practical career support across the UK.</div>
      </footer>
    </div>
  );
}